/**
 * Utilities for managing tenant context in URLs.
 * Only applicable for internal support users who can access multiple tenants.
 */

export const TENANT_PARAM = "tenant";

/**
 * Extract tenant ID from current URL query parameters
 * @returns {string|null} The tenant ID or null if not present
 */
export const getTenantFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get(TENANT_PARAM);
};

/**
 * Build a URL with tenant query parameter
 * @param {string} url - The URL to modify (can be relative or absolute)
 * @param {string} tenantId - The tenant ID to add
 * @returns {string} The URL with tenant parameter
 */
export const buildUrlWithTenant = (url, tenantId) => {
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set(TENANT_PARAM, tenantId);
  return urlObj.pathname + urlObj.search + urlObj.hash;
};

/**
 * Sync tenant ID to current URL without triggering navigation
 * @param {string} tenantId - The tenant ID to sync
 * @param {boolean} replace - If true, replaces current history entry (no back button entry)
 */
export const syncTenantToUrl = (tenantId, replace = false) => {
  const currentTenant = getTenantFromUrl();

  // Only update if tenant is different to avoid unnecessary history entries
  if (currentTenant === tenantId) {
    return;
  }

  const url = buildUrlWithTenant(window.location.href, tenantId);
  if (replace) {
    window.history.replaceState({}, "", url);
  } else {
    window.history.pushState({}, "", url);
  }
};

/**
 * Remove tenant parameter from URL
 * @param {boolean} replace - If true, replaces current history entry
 */
export const removeTenantFromUrl = (replace = true) => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(TENANT_PARAM)) {
    return;
  }

  params.delete(TENANT_PARAM);
  const search = params.toString();
  const url =
    window.location.pathname +
    (search ? `?${search}` : "") +
    window.location.hash;

  if (replace) {
    window.history.replaceState({}, "", url);
  } else {
    window.history.pushState({}, "", url);
  }
};
